import * as R from 'ramda'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { useTranslation } from '../../i18n'
import IconNextSvg from '../../public/static/images/icon-next.svg'
import IconPrevSvg from '../../public/static/images/icon-previous.svg'
import { getFontSize } from '../../utils/cssUtils'
import mq from '../../utils/mediaQuery'
import SvgIcon from '../SvgIcon'
import Dropdown from './Dropdown'
import Page from './Page'

const PaginationWrapper = styled.div`
  display: inline-flex;
  ${mq.mobile} {
    flex-flow: column nowrap;
    align-items: center;
  }
`
const PageList = styled.ul`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin-bottom: -8px;
  padding: 0;
`
const PageSelectWrapper = styled.li`
  display: flex;
  margin-bottom: 8px;
`
const Select = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`
const SelectLabel = styled.span`
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Simple = styled.li`
  height: 32px;
  list-style: none;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 8px 8px 0;
  line-height: 1;
  font-size: ${getFontSize('body2')};
  ${mq.mobile} {
    display: none;
  }
`

const Pagination = ({
  simple,
  showLess,
  total,
  pageSize,
  defaultCurrent,
  current,
  onChange
}) => {
  pageSize = Number(pageSize)

  const { t } = useTranslation('common')

  const currentPage = Number(current || defaultCurrent)

  const pageLinksCount = useMemo(() => (showLess ? 5 : 7), [showLess])

  const halfOfRemaining = useMemo(() => R.divide(R.dec(pageLinksCount), 2), [
    pageLinksCount
  ])

  const totalPages = useMemo(() => Math.ceil(total / pageSize), [
    total,
    pageSize
  ])

  const pageRange = useMemo(
    () =>
      R.lt(totalPages, pageLinksCount)
        ? R.range(1, R.inc(totalPages))
        : R.cond([
            [
              R.lte(R.__, halfOfRemaining),
              () => R.range(1, R.inc(pageLinksCount))
            ],
            [
              R.gte(R.__, totalPages - halfOfRemaining),
              () =>
                R.range(totalPages - R.dec(pageLinksCount), R.inc(totalPages))
            ],
            [
              R.T,
              current =>
                R.range(
                  current - halfOfRemaining,
                  current + R.inc(halfOfRemaining)
                )
            ]
          ])(currentPage),
    [currentPage, totalPages, pageLinksCount, halfOfRemaining]
  )

  const generateOptions = useMemo(
    () =>
      R.map(page => ({ value: page, label: page }), R.range(1, totalPages + 1)),
    [totalPages]
  )

  const noResult = useMemo(() => R.equals(totalPages, 0), [totalPages])

  const isFirstPage = useMemo(() => R.equals(currentPage, 1), [currentPage])

  const isLastPage = useMemo(() => R.equals(currentPage, totalPages), [
    currentPage,
    totalPages
  ])

  const isCurrentPage = R.equals(currentPage)

  return noResult ? null : (
    <PaginationWrapper>
      <PageList>
        <Page
          title={t('commom.previousPage')}
          disabled={isFirstPage}
          onClick={e => {
            R.not(isFirstPage) && onChange(R.dec(currentPage))
          }}
          data-test-id='pagination-prev'
        >
          <SvgIcon
            svg={
              <IconPrevSvg
                css={`
                  height: 14px;
                `}
              />
            }
          />
        </Page>
        {simple ? (
          <Simple data-test-id='pagination-simple'>
            {currentPage} / {totalPages}
          </Simple>
        ) : (
          pageRange.map(page => (
            <Page
              title={page}
              active={isCurrentPage(page)}
              tabIndex={0}
              key={page}
              onClick={e => {
                R.not(isCurrentPage(page)) && onChange(page)
              }}
              data-test-id='pagination-number'
            >
              {page}
            </Page>
          ))
        )}
        <Page
          title={t('common.nextPage')}
          disabled={isLastPage}
          onClick={e => {
            R.not(isLastPage) && onChange(R.inc(currentPage))
          }}
          data-test-id='pagination-next'
        >
          <SvgIcon
            svg={
              <IconNextSvg
                css={`
                  height: 14px;
                `}
              />
            }
          />
        </Page>
        {!simple && totalPages > 1 && (
          <PageSelectWrapper>
            <Select>
              <SelectLabel>{t('common.goTo')}</SelectLabel>
              <Dropdown
                onChange={onChange}
                value={currentPage}
                options={generateOptions}
              />
              <SelectLabel>{t('common.page')}</SelectLabel>
            </Select>
          </PageSelectWrapper>
        )}
      </PageList>
    </PaginationWrapper>
  )
}

export default React.memo(Pagination)
